import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Grid,
  Card,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  CreditCard as RechargeIcon,
  VerifiedUser as VerifiedUserIcon,
  ManageAccounts as ManageAccountsIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuthStore } from '../stores/authStore';

interface AdminOverview {
  total_users: number;
  total_boys: number;
  total_girls: number;
  active_users: number;
  inactive_users: number;
  premium_users: number;
  verified_profiles: number;
  pending_reviews: number;
  sent_back_reviews: number;
  approved_profiles: number;
  pending_recharges: number;
  total_recharge_amount: number;
  total_recharge_count: number;
  total_visitors: number;
}

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverview | null>(null);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        setLoading(true);
        const response = await api.get('/users/admin/overview');
        setOverview(response.data);
      } catch (error: any) {
        toast.error(error.response?.data?.detail || 'Failed to load admin overview');
      } finally {
        setLoading(false);
      }
    };

    loadOverview();
  }, []);

  return (
    <AdminLayout currentPage="dashboard">
      <Box>
        <Paper
          sx={{
            borderRadius: 4,
            border: '1px solid #ead4b5',
            background: 'linear-gradient(135deg, rgba(123, 0, 17, 0.95) 0%, rgba(158, 27, 45, 0.9) 70%, rgba(205, 156, 109, 0.82) 100%)',
            color: 'white',
            p: { xs: 2, md: 3 },
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 900 }}>Admin Dashboard</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.88)', mt: 0.7 }}>
            Monitor operations, pending queues, and take actions from one control center.
          </Typography>
        </Paper>

        {loading ? (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: '#7b0011' }} />
          </Box>
        ) : overview ? (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                ['Total Users', overview.total_users],
                ['Active Boys', overview.total_boys ?? 0],
                ['Active Girls', overview.total_girls ?? 0],
                ['Verified Profiles', overview.verified_profiles],
                ['Pending Reviews', overview.pending_reviews],
                ['Pending Recharges', overview.pending_recharges],
                ['Premium Users', overview.premium_users],
                ['Inactive Users', overview.inactive_users],
                ['Total Recharge Earned', `${overview.total_recharge_amount.toLocaleString('en-IN')} Points`],
                ['Recharges Approved', overview.total_recharge_count],
                ['Website Visitors', overview.total_visitors],
              ].map(([label, value]) => (
                <Grid item xs={6} md={4} lg={2} key={String(label)}>
                  <Card sx={{ borderRadius: 3, border: '1px solid #efddc4', boxShadow: '0 8px 18px rgba(69, 20, 8, 0.06)' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#7b6c64', fontWeight: 700 }}>{label}</Typography>
                      <Typography sx={{ fontSize: '1.6rem', color: '#7b0011', fontWeight: 900 }}>{value}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              {[
                {
                  title: 'Recharge Management',
                  desc: 'Manage QR codes, pending recharges, and wallet approvals.',
                  icon: <RechargeIcon sx={{ fontSize: 42 }} />,
                  bg: 'linear-gradient(160deg, #f4fff2 0%, #e9f9e5 100%)',
                  border: '#d5ecd0',
                  color: '#2f7d32',
                  path: '/admin/recharge-management',
                  cta: 'Open Recharge Management',
                },
                {
                  title: 'Profile Reviews',
                  desc: 'Approve completed profiles and push corrections back to users.',
                  icon: <VerifiedUserIcon sx={{ fontSize: 42 }} />,
                  bg: 'linear-gradient(160deg, #fff3f4 0%, #fde9eb 100%)',
                  border: '#f2d3d7',
                  color: '#a31227',
                  path: '/admin/profile-reviews',
                  cta: 'Open Profile Reviews',
                },
                {
                  title: 'User Management',
                  desc: 'Search users, update status, and manage account controls.',
                  icon: <ManageAccountsIcon sx={{ fontSize: 42 }} />,
                  bg: 'linear-gradient(160deg, #f2f8ff 0%, #e7f0fc 100%)',
                  border: '#d4e3f8',
                  color: '#1d4f8f',
                  path: '/admin/users',
                  cta: 'Open User Management',
                },
                {
                  title: 'Registration Fields',
                  desc: 'Configure which fields appear in profile completion and signup.',
                  icon: <SettingsSuggestIcon sx={{ fontSize: 42 }} />,
                  bg: 'linear-gradient(160deg, #f9f3ff 0%, #f1e8fc 100%)',
                  border: '#e2d4f7',
                  color: '#5a2d91',
                  path: '/admin/registration-fields',
                  cta: 'Configure Registration Fields',
                },
                {
                  title: 'User Control Center',
                  desc: 'Inspect timeline events, role permissions, and admin actions.',
                  icon: <TimelineIcon sx={{ fontSize: 42 }} />,
                  bg: 'linear-gradient(160deg, #f2fffb 0%, #e6f9f3 100%)',
                  border: '#d0efe4',
                  color: '#1c7a66',
                  path: '/admin/user-control-center',
                  cta: 'Open User Control Center',
                },
              ].filter((item) => {
                const currentRole = String(userRole || user?.role || '').toUpperCase();
                if (currentRole !== 'SUPPORT_ADMIN' || !user?.admin_permissions?.length) return true;
                const permissionByPath: Record<string, string> = {
                  '/admin/recharge-management': 'recharge-management',
                  '/admin/profile-reviews': 'profile-reviews',
                  '/admin/users': 'user-management',
                  '/admin/registration-fields': 'registration-fields',
                  '/admin/user-control-center': 'user-control-center',
                };
                return user.admin_permissions.includes(permissionByPath[item.path]);
              }).map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.title}>
                  <Card sx={{ borderRadius: 3, border: `1px solid ${item.border}`, background: item.bg, height: '100%' }}>
                    <CardContent sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr auto', gap: 1.1, height: '100%' }}>
                      <Box sx={{ color: item.color }}>{item.icon}</Box>
                      <Typography sx={{ fontWeight: 900, color: '#23201e' }}>{item.title}</Typography>
                      <Typography sx={{ color: '#6f635c', fontSize: '0.9rem' }}>{item.desc}</Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate(item.path)}
                        sx={{ backgroundColor: '#7b0011', '&:hover': { backgroundColor: '#5f000e' }, fontWeight: 800 }}
                      >
                        {item.cta}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}

        {!loading && !overview && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Unable to load dashboard overview. Please refresh and try again.
          </Alert>
        )}
      </Box>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
